import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { Box, Stack, Typography, IconButton, Chip, Avatar } from '@mui/material';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { useDispatch, useSelector } from 'react-redux';
import { theme } from '../../../../constants/theme';
import { ThspianAvatar } from '../../../Avatar/ThspianAvatar';
import { Juror } from '../../../../redux/contest/types';
import { inviteJury, deleteJury, emailJuror, inviteExternalJuror } from '../../../../redux/jury/action';
import CustomButton from '../../../Button/CustomButton';
import _ from 'lodash';
import { searchUser } from '../../../../redux/user/actions';
import { userSearchSelector, searchingUserSearchSelector } from '../../../AvatarGroup/selector';
import { userProfileSelector } from '../../../../selectors/selectors';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { CheckCircle, Pending, Close as CloseIcon } from '@mui/icons-material';
import { Table, ColumnsData } from '../../../../components/Tables/Table';
import { AssignSubmissionsDialogProps } from './AssignSubmissionsDialog';
import { Spinner } from '../../../Loading/Spinner';
import { SegmentedCircle } from '../../../Common/SegmentedCircle';
import { IUserSummary } from '../../../../types/global/helper';
import { IInviteExternalJurorPayload } from '../../../../redux/jury/types';

interface ContestJurorsTabProps {
    jurors: Juror[];
    contestId: string;
    preselectionEnabled?: boolean;
}

const AssignSubmissionsDialog = lazy<React.FC<AssignSubmissionsDialogProps>>(() =>
    //@ts-ignore
    import('./AssignSubmissionsDialog').then(module => {
        return { default: module.AssignSubmissionsDialog };
    }),
);

export const ContestJurorsTab: React.FC<ContestJurorsTabProps> = ({ jurors, contestId, preselectionEnabled }) => {
    const [searchValue, setSearchValue] = useState('');
    const [searchResults, setSearchResults] = useState<IUserSummary[]>([]);
    const [, setSearching] = useState(false);
    const [externalEmails, setExternalEmails] = useState<string[]>([]);
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const userResults = useSelector(userSearchSelector);
    const searchingUser = useSelector(searchingUserSearchSelector);
    const userProfile = useSelector(userProfileSelector);
    const alreadySelected = (user: IUserSummary) => {
        return !!jurors.some(jury => jury && jury.juror && jury.juror._id === user._id);
    };
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [assignDialogJuror, setAssignDialogJuror] = useState<Juror | null>(null);

    const handleSearch = useRef(
        _.debounce((searchTerm: string) => {
            setSearching(true);
            if (searchTerm.length > 3 && !searchingUser) {
                dispatch(searchUser({ searchTerm, withJuryQuery: true, contestId }));
            }
        }, 1000),
    ).current;

    const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        // Check if the last character is a comma, indicating a complete email
        if (value.endsWith(',')) {
            const emails = value
                .split(',')
                .map(email => email.trim())
                .filter(email => {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    return emailRegex.test(email);
                });

            // If we have a new valid email, add it to the list and clear the input
            const newEmail = emails[emails.length - 1];
            if (newEmail && !externalEmails.includes(newEmail)) {
                setExternalEmails(prev => [...prev, newEmail]);
                setSearchValue(''); // Clear the input after adding email
                return;
            }
        }

        setSearchValue(value);

        // Parse emails from the input for display purposes
        const emails = value
            .split(',')
            .map(email => email.trim())
            .filter(email => {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                return emailRegex.test(email);
            });

        // If there are valid emails and no search results, don't search for users
        if (emails.length > 0 && value.includes(',')) {
            return;
        }

        handleSearch(value);
    };

    useEffect(() => {
        if (userResults) {
            setSearchResults(userResults);
            setSearching(false);
        }
    }, [userResults]);

    const handleInviteJury = (userId: string) => {
        dispatch(inviteJury({ contest: contestId, juries: [userId], bypass: true }));
    };

    const handleAssignSubmissions = (jurorId: string) => {
        const juror = jurors.find(j => j.juror._id === jurorId);
        if (juror && juror.accepted_invite) {
            setAssignDialogJuror(juror);
            setAssignDialogOpen(true);
        }
    };

    const handleCancelDuty = (juryId: string) => {
        const juror = jurors.find(j => j._id === juryId);
        if (juror && juror?.total_assigned > 0) {
            return;
        }
        dispatch(deleteJury({ documentId: juryId }));
    };

    const handleRemoveJuror = (juryId: string) => {
        const juror = jurors.find(j => j._id === juryId);
        if (juror && juror?.total_assigned > 0) {
            return;
        }
        dispatch(deleteJury({ documentId: juryId }));
    };

    const handleEmailJuror = (juryId: string) => {
        dispatch(emailJuror({ userId: juryId, contestId, inviterName: `${userProfile.firstName}` }));
    };

    const handleClearSearch = () => {
        setSearchValue('');
        setSearchResults([]);
        setExternalEmails([]);
    };

    const handleRemoveEmail = (emailToRemove: string) => {
        const updatedEmails = externalEmails.filter(email => email !== emailToRemove);
        setExternalEmails(updatedEmails);
    };

    const handleInviteExternalJurors = () => {
        if (externalEmails.length === 0 || !userProfile) return;

        const payload: IInviteExternalJurorPayload = {
            contest: contestId,
            inviterName: `${userProfile.firstName} ${userProfile.lastName}`,
            externalJuries: externalEmails.map(email => ({
                email,
                name: 'there', // Leave name empty as requested
            })),
        };

        dispatch(inviteExternalJuror(payload));
        handleClearSearch();
    };

    const columnsData: ColumnsData[] = [
        {
            field: 'juror',
            label: 'Juror',
            type: 'custom',
            sortable: true,
            flex: 1,
            customCell: (params: Juror) =>
                params.juror ? (
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Box sx={{ position: 'relative' }}>
                            {params.accepted_invite ? (
                                <CheckCircle
                                    sx={{
                                        position: 'absolute',
                                        top: -5,
                                        left: -5,
                                        color: theme[themeStyle].true_white,
                                        border: `1px solid ${theme[themeStyle].gray_message}`,
                                        borderRadius: '50%',
                                        zIndex: 1,
                                    }}
                                />
                            ) : (
                                <Pending
                                    sx={{
                                        position: 'absolute',
                                        top: -5,
                                        left: -5,
                                        color: theme[themeStyle].tag_white,
                                        border: `1px solid ${theme[themeStyle].gray_message}`,
                                        borderRadius: '50%',
                                        zIndex: 1,
                                    }}
                                />
                            )}
                            <ThspianAvatar
                                user={params.juror}
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                }}
                            />
                        </Box>
                        <Stack>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 14,
                                }}>
                                {params.juror.firstName} {params.juror.lastName}
                            </Typography>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 12,
                                }}>
                                @{params.juror.userName}
                            </Typography>
                        </Stack>
                    </Stack>
                ) : (
                    <Avatar />
                ),
        },
        {
            field: 'judgment_percentage',
            label: 'Judgment Progress',
            type: 'custom',
            sortable: true,
            width: 200,
            customCell: (params: Juror) => (
                <Box sx={{ width: '100%', alignItems: 'center' }}>
                    <SegmentedCircle
                        percentage={params.judgment_percentage}
                        textColor={theme[themeStyle].tag_white}
                        size={32}
                        segments={28}
                        strokeWidth={0.9}
                        tickLength={5}
                        fontSizeRatio={0.2}
                    />
                </Box>
            ),
        },
        {
            field: 'total_assigned',
            label: 'Total Assigned',
            type: 'text',
            sortable: true,
            width: 150,
        },
        {
            field: 'judged_submissions',
            label: 'Completed',
            type: 'text',
            sortable: true,
            width: 100,
        },
        {
            field: 'accepted_invite',
            label: 'Invitation Status',
            type: 'custom',
            sortable: true,
            width: 150,
            customCell: (params: Juror) => (
                <Typography
                    style={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        color: params.accepted_invite ? theme[themeStyle].true_black : theme[themeStyle].tag_white,
                        fontSize: 12,
                        backgroundColor: params.accepted_invite
                            ? theme[themeStyle].switchGreen
                            : theme[themeStyle].red_status_indicator,
                        padding: 10,
                        borderRadius: 1,
                    }}>
                    {params.accepted_invite ? 'Accepted' : 'Pending'}
                </Typography>
            ),
        },
        {
            field: 'actions',
            label: 'Actions',
            type: 'action' as const,
            width: 100,
            sortable: false,
        },
    ];

    const moreMenuItems = [
        {
            title: 'Assign Submissions',
            id: 'assign-submission',
            onClick: handleAssignSubmissions,
        },
        {
            title: 'Cancel Duty',
            id: 'cancel-duty',
            onClick: handleCancelDuty,
        },
        {
            title: 'Remove Juror',
            id: 'remove-juror',
            onClick: handleRemoveJuror,
        },
        {
            title: 'Email Juror',
            id: 'email-juror',
            onClick: handleEmailJuror,
        },
    ];

    return (
        <Box>
            {/* Search input and dropdown container */}
            <Box sx={{ position: 'relative', width: { xs: '100%', sm: '60%' }, mb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                    <Box
                        sx={{
                            flex: 1,
                            position: 'relative',
                            background: theme[themeStyle].deep_grey_lighter,
                            borderRadius: 2,
                            border: `1px solid ${theme[themeStyle].tag_white}`,
                            minHeight: 50,
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px 12px',
                            '&:hover': {
                                borderColor: theme[themeStyle].highlight,
                            },
                            '&:focus-within': {
                                borderColor: theme[themeStyle].highlight,
                            },
                        }}>
                        {/* Email chips */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mr: 1 }}>
                            {externalEmails.map((email, index) => (
                                <Chip
                                    key={index}
                                    label={email}
                                    onDelete={() => handleRemoveEmail(email)}
                                    deleteIcon={<CloseIcon sx={{ color: theme[themeStyle].red_status_indicator }} />}
                                    size="small"
                                    sx={{
                                        backgroundColor: theme[themeStyle].secondaryBg,
                                        borderRadius: 1,
                                        color: theme[themeStyle].tag_white,
                                        fontSize: '11px',
                                        height: 24,
                                        '& .MuiChip-deleteIcon': {
                                            color: theme[themeStyle].true_white,
                                            fontSize: '14px',
                                        },
                                        '& .MuiChip-label': {
                                            paddingLeft: '8px',
                                            paddingRight: '4px',
                                        },
                                    }}
                                />
                            ))}
                        </Box>

                        {/* Text input */}
                        <input
                            type="text"
                            value={searchValue}
                            onChange={handleSearchInputChange}
                            placeholder={
                                externalEmails.length > 0
                                    ? 'Add more emails...'
                                    : 'Search and add jurors or enter emails separated by comma to invite external users'
                            }
                            style={{
                                flex: 1,
                                border: 'none',
                                outline: 'none',
                                background: 'transparent',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                color: theme[themeStyle].tag_white,
                                fontSize: '14px',
                                minWidth: 0,
                            }}
                        />

                        {/* Clear button */}
                        {searchValue && (
                            <IconButton
                                onClick={handleClearSearch}
                                size="small"
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    padding: '4px',
                                    ml: 1,
                                }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                    {externalEmails.length > 0 && (
                        <CustomButton
                            onClick={handleInviteExternalJurors}
                            label="Invite External Jurors"
                            customStyle={{
                                minWidth: 150,
                                fontSize: 12,
                                padding: '8px 16px',
                                height: 50,
                            }}
                        />
                    )}
                </Stack>
                {/* Dropdown for search results */}
                {searchValue.length > 2 && searchResults.length > 0 && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            width: '100%',
                            zIndex: 10,
                            background: theme[themeStyle].deep_grey_lighter,
                            border: `1px solid ${theme[themeStyle].gray_message}`,
                            borderRadius: 2,
                            boxShadow: 3,
                            mt: 1,
                            maxHeight: 200,
                            overflowY: 'auto',
                        }}>
                        {searchResults.map((user: IUserSummary, idx: number) => (
                            <Stack
                                key={user._id || idx}
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{
                                    px: 2,
                                    py: 1,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        background: theme[themeStyle].gray_message,
                                    },
                                }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <ThspianAvatar user={user} sx={{ width: 32, height: 32 }} />
                                    <Box>
                                        <Typography sx={{ color: theme[themeStyle].tag_white, fontSize: 14 }}>
                                            {user.firstName} {user.lastName}
                                        </Typography>
                                        <Typography sx={{ color: theme[themeStyle].tag_white, fontSize: 12 }}>
                                            @{user.userName}
                                        </Typography>
                                    </Box>
                                </Stack>
                                <CustomButton
                                    onClick={() => handleInviteJury(user._id)}
                                    label={alreadySelected(user) ? 'Invited' : 'Invite'}
                                    disabled={alreadySelected(user)}
                                    customStyle={{ minWidth: 70, fontSize: 12, padding: '2px 8px' }}
                                />
                            </Stack>
                        ))}
                    </Box>
                )}
            </Box>
            <Box
                sx={{
                    backgroundColor: theme[themeStyle].secondaryBg,
                    borderRadius: 2,
                    padding: 3,
                    '& .MuiDataGrid-root': {
                        backgroundColor: theme[themeStyle].secondaryBg,
                    },
                    '& .MuiDataGrid-filler': {
                        backgroundColor: theme[themeStyle].secondaryBg,
                    },
                    '& .MuiDataGrid-virtualScroller': {
                        backgroundColor: theme[themeStyle].secondaryBg,
                    },
                }}>
                <Table
                    columns={columnsData}
                    staticData={jurors}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    tableType="other"
                    rowHeight={80}
                    headerHeight={60}
                    onFullscreenMenuClick={(type, juror) => {
                        if (type === 'assign-submission') handleAssignSubmissions(juror.juror._id);
                        if (type === 'cancel-duty') handleCancelDuty(juror._id);
                        if (type === 'remove-juror') handleRemoveJuror(juror._id);
                        if (type === 'email-juror') handleEmailJuror(juror.juror._id);
                    }}
                    fullscreenMoreMenu={moreMenuItems}
                    fullscreenMoreIcon
                    textSx={{ fontSize: 14 }}
                    sx={{
                        minHeight: 450,
                        backgroundColor: theme[themeStyle].secondaryBg,
                        '& .MuiDataGrid-root': {
                            backgroundColor: theme[themeStyle].secondaryBg,
                        },
                        '& .MuiDataGrid-filler': {
                            backgroundColor: theme[themeStyle].secondaryBg,
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            backgroundColor: theme[themeStyle].secondaryBg,
                        },
                    }}
                />
            </Box>
            {/* Assign Submissions Dialog */}
            <Suspense fallback={<Spinner />}>
                {assignDialogJuror && (
                    <AssignSubmissionsDialog
                        open={assignDialogOpen}
                        onClose={() => setAssignDialogOpen(false)}
                        juror={assignDialogJuror}
                        contestId={contestId}
                        preselectionEnabled={preselectionEnabled}
                    />
                )}
            </Suspense>
        </Box>
    );
};
